<template>
  <!-- Top tabs -->
  <v-tabs v-model="currentTab" density="compact" center-active show-arrows>
    <v-tab value="content_settings">
      <span>Content</span>
    </v-tab>
    <v-tab value="style_settings">
      <span>Styles</span>
    </v-tab>
  </v-tabs>

  <!-- Main content -->
  <v-window v-model="currentTab" :touch="false">
    <!-- Content settings -->
    <v-window-item value="content_settings">
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-text-field
              label="Header Text"
              v-model="sectionSettings.header.text"
              @update:model-value="updateHeaderSetting('text', $event)"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              label="Select features"
              v-model="sectionSettings.features"
              :items="availableFeatures"
              multiple
              clearable
              :rules="[maxSelectionRule, requiredRule]"
            >
              <template v-slot:selection="{ item }">
                <v-chip closable @click:close="removeFeature(item.value)">{{ item.value }}</v-chip>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item
                  :disabled="
                    sectionSettings.features.length >= 3 &&
                    !sectionSettings.features.includes(item.value)
                  "
                >
                  <v-list-item-title>
                    <v-checkbox
                      :model-value="sectionSettings.features.includes(item.value)"
                      :label="item.value"
                      hide-details
                      :disabled="
                        sectionSettings.features.length >= 3 &&
                        !sectionSettings.features.includes(item.value)
                      "
                      @change="toggleFeature(item.value)"
                    />
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>

    <!-- Style settings -->
    <v-window-item value="style_settings">
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>Colors</v-card-title>
              <v-card-text class="d-flex flex-column">
                <ColorSettings
                  :excluded="['background', 'backgroundHover', 'colorHover']"
                  :model-value="sectionSettings.header.colors"
                  @update:model-value="updateHeaderSetting('colors', $event)"
                />
                <ColorSettings
                  :excluded="['color', 'colorHover', 'backgroundHover']"
                  :model-value="sectionSettings.generalSettings.backgroundColors"
                  @update:model-value="updateBackground($event)"
                />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12">
            <v-card>
              <v-card-title>Typography</v-card-title>
              <v-card-text>
                <TypographySettings
                  :excluded="[]"
                  :model-value="sectionSettings.header.typography"
                  @update:model-value="updateHeaderSetting('typography', $event)"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-window-item>
  </v-window>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ColorSettings, TypographySettings } from '@jazel/jazel-vuetify'
import type { IColorSettings } from '@jazel/jazel-vuetify'
import { availableFeatures } from '@/constants'
import type { IFeaturesSectionSettings } from '@/types'
import {
  getDefaultFeaturesSectionGeneralSettings,
  getDefaultFeaturesSectionHeaderSettings,
  getDefaultFeaturesSectionSettings,
} from '@/stores/utils'

const props = defineProps<{
  section: IFeaturesSectionSettings
}>()

const emit = defineEmits<{
  (event: 'update:section-settings', value: IFeaturesSectionSettings): void
}>()

const sectionSettings = ref<IFeaturesSectionSettings>(
  props.section && props.section.header ? props.section : getDefaultFeaturesSectionSettings(),
)
const currentTab = ref<string>('content_settings')

const updateHeaderSetting = (key: string, value: any) => {
  sectionSettings.value.header = {
    ...sectionSettings.value.header,
    [key]: value,
  }

  emit('update:section-settings', sectionSettings.value)
}

const updateBackground = (value: IColorSettings) => {
  sectionSettings.value.generalSettings.backgroundColors = value

  emit('update:section-settings', sectionSettings.value)
}

const removeFeature = (feature: string) => {
  sectionSettings.value.features = sectionSettings.value.features.filter((f) => f !== feature)

  emit('update:section-settings', sectionSettings.value)
}

const toggleFeature = (feature: string) => {
  const index = sectionSettings.value.features.indexOf(feature)
  if (index === -1) {
    sectionSettings.value.features.push(feature)
  } else {
    sectionSettings.value.features.splice(index, 1)
  }

  emit('update:section-settings', sectionSettings.value)
}

const maxSelectionRule = (v: string[] | null | undefined) => {
  if (!v) return true // Skip validation if null or undefined
  return v.length < 3 || 'Max 3 features allowed'
}

const requiredRule = (v: string[] | null | undefined) => {
  if (!v) return 'At least 1 feature required' // Fail validation if null/undefined
  return v.length > 0 || 'At least 1 feature required'
}

onMounted(() => {
  if (!sectionSettings.value.header) {
    sectionSettings.value.header = getDefaultFeaturesSectionHeaderSettings()
  }

  if (!sectionSettings.value.features) {
    sectionSettings.value.features = []
  }

  if (!sectionSettings.value.generalSettings) {
    sectionSettings.value.generalSettings = getDefaultFeaturesSectionGeneralSettings()
  }
})
</script>

<style scoped>
:deep(.v-selection-control .v-label--clickable) {
  flex-grow: 1;
}
</style>
